import PropTypes from 'prop-types'
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react'
import { Card, CardBody, CardHeader } from 'reactstrap'
import InputImageField from 'components/molecules/input-image-field'
import LoaderButton from 'components/molecules/loader-button'

const propTypes = {
  title                 : PropTypes.string,
  onFileSelectionError  : PropTypes.func,
  onSuccessAttachImages : PropTypes.func
}

const defaultProps = {
  title                 : '',
  onSuccessAttachImages : () => { },
  onFileSelectionError  : () => {}
}

/**
 * @param {string} title - a string that's going to be the header of the upload card
 * @param {func} onSuccessAttachImages - a function that will be called when attaching image is successful
 * @param {func} onFileSelectionError - a function that will be passed to InputImageField, and will be called when the selected file doesn't match the requirement
 */

const AttachImageCard = forwardRef(({
  title,
  onSuccessAttachImages,
  onFileSelectionError,
  ...props
}, ref) => {
  const [ chosenImages, setChosenImages ]                   = useState([])
  const [ showAttachImageButton, setShowAttachImageButton ] = useState(false)
  const [ isAttachImageLoading, setIsAttachImageLoading ]   = useState(false)

  useEffect(() => {
    if (chosenImages.length > 0) {
      setShowAttachImageButton(true)
    }
  },[ setShowAttachImageButton, chosenImages ])

  const handleClickAttachImage = () => {
    setIsAttachImageLoading(true)
    const imageData = { images: chosenImages }
    onSuccessAttachImages(imageData.images)
    setIsAttachImageLoading(false)
    setShowAttachImageButton(false)
  }

  const handleClearImages = useCallback(() => {
    fileInputRef.current.resetFile()
    setIsAttachImageLoading(false)
    setShowAttachImageButton(false)
  },[])

  useImperativeHandle(ref, () => ({
    resetImages() {
      fileInputRef.current.value = ''
      handleClearImages()
    }
  }), [ handleClearImages ])

  const fileInputRef = useRef()

  return (
    <Card>
      {
        title && (
          <CardHeader>
            <h4><b>{ title }</b></h4>
          </CardHeader>
        )
      }
      <CardBody>
        <InputImageField
          onSelectImages={ setChosenImages }
          onFileSelectionError={ onFileSelectionError }
          ref={ fileInputRef }
          { ... props }
        />
        {
          showAttachImageButton && (
            <LoaderButton
              className='mt-3'
              color='primary'
              onClick={ handleClickAttachImage }
              isLoading={ isAttachImageLoading }
              disabled={ isAttachImageLoading }
              text = 'Attach Image(s)'
            />
          )
        }
      </CardBody>
    </Card>
  )
})

AttachImageCard.propTypes    = propTypes
AttachImageCard.defaultProps = defaultProps

// because using forwardRef
AttachImageCard.displayName = 'AttachImageCard'

export default AttachImageCard
